import { useParams, Link } from "react-router-dom";
import { comics } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  const getCategorySlug = (cat: string) => {
    const map: Record<string, string> = { "恋爱": "romance", "奇幻": "fantasy", "热血": "action", "悬疑": "thriller" };
    return map[cat] || "romance";
  };

  const jsonLd = comic
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "http://uhomekj.com/" },
              { "@type": "ListItem", position: 2, name: `${comic.category}漫画`, item: `http://uhomekj.com/category/${getCategorySlug(comic.category)}` },
              { "@type": "ListItem", position: 3, name: comic.title, item: `http://uhomekj.com/comic/${comic.id}` }
            ]
          },
          {
            "@type": "Article",
            headline: `${comic.title} - 韩漫大全漫画网`,
            description: comic.description,
            author: { "@type": "Person", name: comic.author },
            datePublished: "2026-01-01",
            dateModified: comic.updateTime,
            publisher: { "@type": "Organization", name: "韩漫大全漫画网", url: "http://uhomekj.com/" },
            url: `http://uhomekj.com/comic/${comic.id}`
          }
        ]
      }
    : undefined;

  usePageMeta({
    title: comic ? `${comic.title}_韩漫大全漫画网下拉式免费无删减在线` : "韩漫大全漫画网",
    description: comic ? `${comic.title} - ${comic.description.substring(0, 120)}，作者${comic.author}，${comic.status}中，更新至第${comic.latestChapter}话。韩漫大全漫画网提供下拉式免费无删减在线阅读。` : "韩漫大全漫画网",
    keywords: comic ? `${comic.title},韩漫大全漫画网,${comic.category}韩漫,${comic.tags.join(",")},下拉式免费` : "韩漫大全漫画网",
    canonical: `http://uhomekj.com/comic/${id}`,
    ogType: comic ? "article" : undefined,
    ogImage: comic?.coverImage,
    jsonLd,
  });

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics.filter((c) => c.id !== comic.id && c.category === comic.category).slice(0, 4);

  return (
    <>
      <Navbar />

      <section className="relative w-full h-[350px] md:h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={comic.coverImage}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70"></div>
        </div>
        <div className="relative z-10 h-full w-full px-4 md:px-6 flex flex-col justify-end pb-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight">
              {comic.title}
            </h1>
            <p className="text-white/70 text-sm md:text-base mt-3">
              作者：{comic.author} · {comic.status} · {comic.views}阅读
            </p>
          </div>
        </div>
      </section>

      <main className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <Breadcrumb items={[
              { label: "首页", href: "/" },
              { label: `${comic.category}漫画`, href: `/category/${getCategorySlug(comic.category)}` },
              { label: comic.title }
            ]} />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="flex items-start gap-2 mb-4">
                  <span className="text-accent-500 text-3xl font-serif">&ldquo;</span>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">作品简介</h2>
                </div>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
                <div className="flex flex-wrap gap-2 mt-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-900 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-primary-500">{comic.rating}</div>
                    <div className="text-xs text-foreground-400">评分</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-950">{comic.latestChapter}</div>
                    <div className="text-xs text-foreground-400">总章节</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-foreground-950">{comic.views}</div>
                    <div className="text-xs text-foreground-400">累计阅读</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-accent-500">{comic.status}</div>
                    <div className="text-xs text-foreground-400">连载状态</div>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">章节列表</h2>
                <div className="bg-background-100 rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    {comic.chapters.slice(0, 30).map((ch) => (
                      <button
                        key={ch.number}
                        className="w-full flex items-center justify-between px-5 py-3 border-b border-background-200/70 last:border-0 hover:bg-background-200/70 transition-colors cursor-pointer text-left"
                      >
                        <span className="text-sm text-foreground-800">{ch.title}</span>
                        <span className="text-xs text-foreground-400">{ch.updateTime}</span>
                      </button>
                    ))}
                  </div>
                  {comic.chapters.length > 30 && (
                    <div className="text-center py-3 bg-background-200/50">
                      <span className="text-xs text-foreground-500">
                        还有 {comic.chapters.length - 30} 话，持续更新中...
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:w-72 flex-shrink-0">
              <div className="rounded-lg overflow-hidden mb-6 sticky top-24">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full aspect-[3/4] object-cover object-top rounded-lg"
                />
                <button
                  className="mt-4 w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium text-center block hover:bg-primary-600 transition-all cursor-pointer"
                >
                  开始阅读第1话
                </button>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <section className="mt-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group block cursor-pointer"
                    data-product-shop=""
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <span className="text-white text-xs">第{rc.latestChapter}话</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-0.5">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}